import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Mascot from "@/components/Mascot";
import { Users, BookOpen, CalendarCheck, Sparkles, TrendingUp, ClipboardList, Loader2, ArrowRight } from "lucide-react";

const STATS = [
  { key: "total_students", label: "Total Murid", icon: Users, color: "teal" },
  { key: "total_subjects", label: "Mata Pelajaran", icon: BookOpen, color: "sky" },
  { key: "pending_attendance", label: "Absensi Menunggu", icon: CalendarCheck, color: "amber", to: "/guru/absensi" },
  { key: "to_review", label: "Perlu Dicek", icon: Sparkles, color: "indigo", to: "/guru/review" },
];

const COLORS = {
  teal: "bg-teal-50 text-teal-700",
  sky: "bg-sky-50 text-sky-700",
  amber: "bg-amber-50 text-amber-700",
  indigo: "bg-indigo-50 text-indigo-700",
};

export default function GuruDashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/guru").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>;

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <div className="flex items-center gap-4 bg-gradient-to-r from-teal-600 to-teal-500 rounded-3xl p-6 sm:p-8 text-white relative overflow-hidden">
        <div className="absolute -right-6 -bottom-8 opacity-90"><Mascot size={120} className="animate-float" /></div>
        <div className="relative z-10">
          <h1 className="font-display text-2xl sm:text-3xl font-bold">Halo, Bu/Pak Guru! 👋</h1>
          <p className="mt-2 text-teal-50 max-w-lg">Berikut ringkasan kelas hari ini. Tingkat kehadiran dan tugas yang perlu diperiksa ada di bawah.</p>
          <div className="mt-4 inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-4 py-2 text-sm">
            <TrendingUp className="w-4 h-4 text-amber-300" /> Kehadiran hari ini: <strong>{data.attendance_rate}%</strong>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 stagger">
        {STATS.map((s) => (
          <button key={s.key} onClick={() => s.to && navigate(s.to)} data-testid={`stat-${s.key}`}
            className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all text-left">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${COLORS[s.color]}`}>
              <s.icon className="w-5 h-5" />
            </div>
            <p className="mt-3 font-display text-3xl font-bold text-slate-800">{data[s.key]}</p>
            <p className="text-sm text-slate-500">{s.label}</p>
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <h3 className="font-display text-lg font-semibold text-slate-800 mb-4">Aktivitas Terbaru</h3>
          <div className="space-y-3">
            {data.recent.length === 0 && <p className="text-sm text-slate-400">Belum ada aktivitas.</p>}
            {data.recent.map((n) => (
              <div key={n.id} className="flex items-start gap-3 py-2 border-b border-slate-50 last:border-0">
                <span className="w-2 h-2 rounded-full bg-teal-500 mt-2" />
                <div>
                  <p className="text-sm text-slate-700">{n.message}</p>
                  <p className="text-[11px] text-slate-400">{(n.created_at || "").slice(0, 16).replace("T", " ")}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <h3 className="font-display text-lg font-semibold text-slate-800 mb-4">Aksi Cepat</h3>
          <div className="space-y-2">
            {[
              { label: "Tambah Murid", to: "/guru/murid", icon: Users },
              { label: "Buat Tugas", to: "/guru/tugas", icon: ClipboardList },
              { label: "Verifikasi Absensi", to: "/guru/absensi", icon: CalendarCheck },
              { label: "Pusat Review AI", to: "/guru/review", icon: Sparkles },
            ].map((a) => (
              <button key={a.to} onClick={() => navigate(a.to)} data-testid={`quick-${a.to.split("/").pop()}`}
                className="w-full flex items-center justify-between px-4 py-3 rounded-xl hover:bg-slate-50 transition-colors group">
                <span className="flex items-center gap-3 text-sm font-medium text-slate-700"><a.icon className="w-4 h-4 text-teal-600" />{a.label}</span>
                <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-teal-600 group-hover:translate-x-0.5 transition-all" />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
